/**
 * World generation: chasms, numbers and terrain from seed and coordinates alone.
 * See spec/core-spec.md, sections "Chasms" and "Terrain".
 */
import { COORD_LIMIT, distanceSq, NEIGHBOURS, tileKey } from "./coords.ts";
import { mix32 } from "./hash.ts";
import { valueNoise16 } from "./noise.ts";

export const Terrain = {
  water: 0,
  grass: 1,
  forest: 2,
  mountain: 3,
} as const;
export type Terrain = (typeof Terrain)[keyof typeof Terrain];

export const TERRAIN_NAMES = ["water", "grass", "forest", "mountain"] as const;

/**
 * The version of the rules in spec/core-spec.md sections 3 to 6. Every run record carries it, so a
 * run can always be replayed with the rules it was played under. Bumped only when a rule changes
 * after launch; before launch the rules change in place.
 */
export const WORLD_VERSION = 1;
export type TerrainName = (typeof TERRAIN_NAMES)[number];

/** Salts are XORed into the seed before hashing so the streams never collide. */
export const CHASM_SALT = 0x4d4f4e53; // "MONS": the bytes of the first name, kept so every board stays the same
export const TERRAIN_SALT_COARSE = 0x54455231; // "TER1"
export const TERRAIN_SALT_FINE = 0x54455232; // "TER2"

export interface WorldConfig {
  /** A tile holds a chasm when mix32(x, y, seed ^ CHASM_SALT) is below this. */
  readonly chasmThreshold: number;
  /** No chasms where x*x + y*y <= safeRadiusSq. */
  readonly safeRadiusSq: number;
  /**
   * Coordinates are valid when -coordLimit < x, y < coordLimit. Absent means COORD_LIMIT, the
   * official limit; vectors lower it to reach the edge by walking. Never above COORD_LIMIT.
   */
  readonly coordLimit?: number;
  /**
   * The chasms of a world made by hand, as the lesson's (decision 0045): when present, a tile holds
   * a chasm exactly when it is listed, and the hash, the threshold and the safe radius are not read.
   */
  readonly chasms?: readonly (readonly [number, number])[];
}

/** The official world: density 0.17 as an unsigned 32-bit threshold, safe zone around the cabin. */
export const DEFAULT_WORLD: WorldConfig = {
  chasmThreshold: 730144440, // floor(0.17 * 2^32)
  safeRadiusSq: 5,
  coordLimit: COORD_LIMIT,
};

/** Noise lattice periods in tiles. */
export const TERRAIN_PERIOD_COARSE = 9;
export const TERRAIN_PERIOD_FINE = 4;

/** Height thresholds in Q16: below water is water, below grass is grass, below forest is forest. */
export const HEIGHT_WATER = 24904; // 0.38
export const HEIGHT_GRASS = 36700; // 0.56
export const HEIGHT_FOREST = 43909; // 0.67

/** Tiles with distance squared at most this are the clearing around the cabin: always grass. */
export const CLEARING_RADIUS_SQ = 2;
/** Tiles with distance squared below this are at least forest. */
export const FOREST_RING_RADIUS_SQ = 20;

export function isChasm(
  seed: number,
  x: number,
  y: number,
  config: WorldConfig = DEFAULT_WORLD,
): boolean {
  if (config.chasms) return listed(config.chasms).has(tileKey(x, y));
  if (distanceSq(x, y) <= config.safeRadiusSq) return false;
  return mix32(x, y, seed ^ CHASM_SALT) < config.chasmThreshold;
}

/** A list of chasms as a set of tile keys, made once for each list. */
const LISTS = new WeakMap<readonly (readonly [number, number])[], ReadonlySet<number>>();
function listed(chasms: readonly (readonly [number, number])[]): ReadonlySet<number> {
  let set = LISTS.get(chasms);
  if (!set) {
    set = new Set(chasms.map(([x, y]) => tileKey(x, y)));
    LISTS.set(chasms, set);
  }
  return set;
}

/** Number of chasms among the eight neighbours, 0 to 8. Defined for every tile. */
export function adjacentChasms(
  seed: number,
  x: number,
  y: number,
  config: WorldConfig = DEFAULT_WORLD,
): number {
  let n = 0;
  for (const [dx, dy] of NEIGHBOURS) {
    if (isChasm(seed, x + dx, y + dy, config)) n += 1;
  }
  return n;
}

/** Terrain height in Q16 before the cabin overrides: a 13/20 and 7/20 blend of two noise octaves. */
export function terrainHeight(seed: number, x: number, y: number): number {
  const coarse = valueNoise16(x, y, TERRAIN_PERIOD_COARSE, seed ^ TERRAIN_SALT_COARSE);
  const fine = valueNoise16(x, y, TERRAIN_PERIOD_FINE, seed ^ TERRAIN_SALT_FINE);
  return Math.floor((13 * coarse + 7 * fine) / 20);
}

/** Terrain of a tile, with the clearing and the forest ring around the cabin applied. */
export function terrainAt(seed: number, x: number, y: number): Terrain {
  const d2 = distanceSq(x, y);
  if (d2 <= CLEARING_RADIUS_SQ) return Terrain.grass;
  let h = terrainHeight(seed, x, y);
  if (d2 < FOREST_RING_RADIUS_SQ && h < HEIGHT_GRASS) h = HEIGHT_GRASS;
  if (h < HEIGHT_WATER) return Terrain.water;
  if (h < HEIGHT_GRASS) return Terrain.grass;
  if (h < HEIGHT_FOREST) return Terrain.forest;
  return Terrain.mountain;
}
